import { getFlattenedNodes, SummaryType } from '@ls/grouping-service';
import { memoize, uniqueByKey } from '@ls/js-utils';
import { createSelector } from 'reselect';
import CalcType from 'state/Constants/CalcType';
import ScaleType from 'state/Constants/ScaleType';
import SelectionState from 'state/selections/SelectionState';
import { selectSelectedAccountEntityId, selectSelectedBenchmarkEntityId, selectSelectedAccountEntity } from 'state/selections/selectors';
import { getEntitySummariesObject, getEntitySummariesObjectForBench } from 'state/util';
import scope from './scope';

export const selectData = (summaryType) => state => state[scope]['data'][summaryType];
export const selectFetchingData = (summaryType) => state => state[scope]['fetchingData'][summaryType];
export const selectFetchingBetaSbsData = (summaryType) => (state) =>
  state[scope]["fetchingBetaSbsData"][summaryType];
export const selectBetaSbsData = (summaryType) => (state) =>
  state[scope]["betaSbsData"][summaryType];

export const selectDefinitions = memoize((selectionState) => {
  if (selectionState === SelectionState.UNCOMMITTED) {
    return state => state[scope]['definitions'][selectionState];
  }
  return createSelector(
    state => state[scope]['definitions'][selectionState],
    (definitions) => {
      const completeDefinitions = definitions.filter(({ complete }) => complete);
      return completeDefinitions.filter(uniqueByKey(completeDefinitions, 'key'));
    },
  );
});

export const selectRowMap = createSelector(
  selectDefinitions(SelectionState.COMMITTED),
  selectData(SummaryType.ABSOLUTE),
  selectData(SummaryType.RELATIVE),
  selectSelectedAccountEntityId(SelectionState.COMMITTED),
  selectSelectedBenchmarkEntityId(SelectionState.COMMITTED),
  selectSelectedAccountEntity(SelectionState.COMMITTED),
  (definitions, absData, relData, accEntityId, bmkEntityId, benchId) => {
    const absoluteNodes = getFlattenedNodes(absData?.root) ?? [];
    const relativeNodes = getFlattenedNodes(relData?.root) ?? [];
    return [...absoluteNodes, ...relativeNodes].reduce((rowMap, node) => {
      const { pathString } = node;
      rowMap[pathString] = rowMap[pathString] ?? { pathString };
      const row = rowMap[pathString];
      definitions
        .filter(({ key }) => !row[key])
        .forEach((definition) => {
          row[definition.key] = findBetaValue(
            node,
            definition,
            accEntityId,
            bmkEntityId,
            benchId?.benchmarkId,
          );
        });
      return rowMap;
    }, {})
  });

export const selectBetaSbsRowMap = createSelector(
  selectDefinitions(SelectionState.COMMITTED),
  selectSelectedAccountEntityId(SelectionState.COMMITTED),
  selectSelectedBenchmarkEntityId(SelectionState.COMMITTED),
  selectBetaSbsData(SummaryType.ABSOLUTE),
  selectBetaSbsData(SummaryType.RELATIVE),
  selectSelectedAccountEntity(SelectionState.COMMITTED),
  (definitions, accEntityId, bmkEntityId, absSbsData, relSbsData, benchId) => {
    const absoluteSbsNodes = getFlattenedNodes(absSbsData?.root) ?? [];
    const relativeSbsNodes = getFlattenedNodes(relSbsData?.root) ?? [];
    return [...absoluteSbsNodes, ...relativeSbsNodes].reduce((rowMap, node) => {
      const { pathString } = node;
      rowMap[pathString] = rowMap[pathString] ?? { pathString };
      const row = rowMap[pathString];
      definitions
        .filter(({ key }) => !row[key])
        .forEach((definition) => {
          row[`${definition.key} - scenario`] = findBetaValue(
            node,
            definition,
            accEntityId,
            bmkEntityId,
            benchId?.benchmarkId,
          );
        });
      return rowMap;
    }, {})
  });



const findBetaValue = (node, definition, accEntityId, bmkEntityId, benchId) => {
  const { betaId, betaFactor, covarianceType, calcType, scaleType } = definition;

  const entityId = calcType === CalcType.Benchmark ? bmkEntityId : accEntityId;
  const benchValue = calcType === CalcType.Benchmark ? 0 : benchId;
  const isRelative = calcType === CalcType.Relative;
  let entitySummariesObject;

  if (benchValue !== null && benchValue !== undefined) {
    entitySummariesObject = getEntitySummariesObjectForBench(node, entityId, benchValue, isRelative) ?? {};
  } else {
    entitySummariesObject = getEntitySummariesObject(node, entityId, isRelative) ?? {};
  }
  let betaSummariesField;
  switch (scaleType) {
    case ScaleType.Contribution:
      betaSummariesField = 'betaSummaries';
      break;
    case ScaleType.WeightedAverage:
      betaSummariesField = 'betaSummariesBucket';
      break;
    default:
      throw new Error(`Unknown scaleType ${scaleType}`);
  }

  const betaSummaries = entitySummariesObject[betaSummariesField];
  const betaSummaryObject = betaSummaries?.find(betaSummary => {
    return betaSummary?.primaryKey?.betaId === betaId
      && betaSummary?.primaryKey?.covarType === covarianceType
      && betaSummary?.primaryKey?.entityId === entityId;
  });
  return betaSummaryObject?.values?.[betaFactor?.toLowerCase()];
};
